import { Injectable } from '@angular/core';
import { Course, Certificate } from './course.service';
import { Course as InstructorCourse } from '../instructor/course.service';
import { AuthService } from '../auth/auth.service';
import { filterCoursesBySearch, sortCourses } from '../shared/utils/course-sort-filter.utils';
import { resolveCourseCompletionStatus } from './course-list.utils';

interface OrgFilterState {
  searchTerm: string;
  sortField: string;
  sortOrder: 'asc' | 'desc';
}

@Injectable({ providedIn: 'root' })
export class CourseListDataService {
  // Organization metadata (for multi-org indicator)
  availableOrganizations: {id: string, name: string}[] = [];

  // Per-section search/sort state
  orgFilterState = new Map<string, OrgFilterState>();

  // Track last accessed module for Continue functionality
  lastAccessedModule: Map<string, string> = new Map();

  constructor(private authService: AuthService) {}

  // ===== Organization Extraction =====

  extractOrganizations(courses: Course[]): void {
    const orgMap = new Map<string, string>();
    courses.forEach(course => {
      const orgId = course.organizationId || course.orgId;
      const orgName = course.organizationName || 'Unknown Organization';
      if (orgId) orgMap.set(orgId, orgName);
    });

    this.availableOrganizations = Array.from(orgMap.entries())
      .map(([id, name]) => ({id, name}))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  getCurrentOrgName(selectedOrgFilter: string): string {
    if (selectedOrgFilter === 'all') return 'All Organizations';
    const org = this.availableOrganizations.find(o => o.id === selectedOrgFilter);
    return org?.name || 'Unknown Organization';
  }

  // ===== Course Status and Progress =====

  // KAN-411: delegates to the shared gate-aware resolver (course-list.utils) so
  // the card and the dashboard "Your Courses" widget can never diverge. Module
  // completion is not genuine completion when the pass gate withholds it; the
  // certificate is the authoritative artifact for certificate-enabled courses.
  getCourseStatus(
    course: Course,
    hasCertificate = false,
  ): 'not-enrolled' | 'enrolled' | 'in-progress' | 'completed' | 'overdue' | 'enrollment-expired' {
    return resolveCourseCompletionStatus(course, hasCertificate);
  }

  getProgressPercentage(course: Course): number {
    return course.computedProgress || 0;
  }

  shouldShowContinueButton(course: Course, hasCertificate = false): boolean {
    const status = this.getCourseStatus(course, hasCertificate);
    const hasProgress = (course.computedProgress || 0) > 0;
    return hasProgress && status !== 'completed';
  }

  getCertificateForCourse(courseId: string, certificates: Certificate[]): Certificate | undefined {
    return certificates.find(cert => cert.courseId === courseId);
  }

  // ===== Progress Tracking =====

  findLastAccessedModule(courseId: string, progressRecords: any[]): void {
    if (!progressRecords || progressRecords.length === 0) return;

    const sortedRecords = progressRecords.sort((a, b) =>
      new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime()
    );

    let lastModuleId = sortedRecords[0].moduleId;
    for (const record of sortedRecords) {
      if (!record.completed) { lastModuleId = record.moduleId; break; }
    }
    if (lastModuleId) this.lastAccessedModule.set(courseId, lastModuleId);
  }

  // ===== Search/Sort =====

  getOrgFilter(key: string): OrgFilterState {
    if (!this.orgFilterState.has(key)) {
      this.orgFilterState.set(key, { searchTerm: '', sortField: 'title', sortOrder: 'asc' });
    }
    return this.orgFilterState.get(key)!;
  }

  setOrgSearch(key: string, term: string): void {
    const state = this.getOrgFilter(key);
    state.searchTerm = term;
  }

  setOrgSort(key: string, field: string, order: 'asc' | 'desc'): void {
    const state = this.getOrgFilter(key);
    state.sortField = field;
    state.sortOrder = order;
  }

  getFilteredSortedCourses(key: string, courses: Course[]): Course[] {
    const { searchTerm, sortField, sortOrder } = this.getOrgFilter(key);
    const filtered = filterCoursesBySearch(courses, searchTerm);
    return sortCourses(filtered, sortField, sortOrder);
  }

  getFilteredSortedInstructorCourses(key: string, courses: InstructorCourse[]): InstructorCourse[] {
    const { searchTerm, sortField, sortOrder } = this.getOrgFilter(key);
    const filtered = filterCoursesBySearch(courses, searchTerm);
    return sortCourses(filtered, sortField, sortOrder);
  }
}
